import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AdminOuParte } from './componentes/AdminOuParte';
import { LimiteDeErro } from './componentes/LimiteDeErro';
import { ProvedorDeDialogos } from './componentes/ui';
import { CascaDoApp } from './layout/CascaDoApp';
import { PerfilDoCliente } from './paginas/cliente/PerfilDoCliente';
import {
  AcaoPorLink,
  Beca,
  CadastroConcluido,
  CadastroPublico,
  CalculadoraPublica,
  Captacao,
  Carteira,
  NegociacoesPontual,
  CentralDeConfiguracoes,
  CriarSenha,
  EmpresaDetalhe,
  Empresas,
  EmpresasDaParte,
  FormularioPublico,
  Notificacoes,
  Passivo,
  PassivoContratos,
  PassivoDaParte,
  PontualDaParte,
  SimulacoesPassivoDaParte,
  Servicos,
  Login,
  Painel,
  Perfil,
  RecuperarSenha,
  PaginaDeAtividades,
  PaginaDeProspeccao,
  PaginaDeRelatorios,
  PessoaDetalhe,
  Pipeline,
  Pessoas,
  RotaProtegida,
  SelecionarEmpresa,
  SimulacaoDoLead,
} from './paginas';

/**
 * A landing page carrega sob demanda porque leva GSAP junto (~130 kB).
 *
 * Quem entra em `/painel` todo dia não deveria pagar o custo de uma biblioteca
 * de animação que só a página de marketing usa. Para quem chega em `/`, o
 * Vite emite o `modulepreload` do chunk da rota, então o salto é de uma
 * requisição paralela — não de uma cascata.
 */
const LandingPage = lazy(() =>
  import('./paginas/LandingPage').then((m) => ({ default: m.LandingPage })),
);

/**
 * Casca da aplicação e mapa de rotas.
 *
 * ## Por que toda rota autenticada fica sob `RotaProtegida`
 *
 * Quando a renovação de token falha, o cliente HTTP **limpa a sessão mas não
 * navega** — de propósito: redirecionar com `window.location` recarregaria a
 * página, perdendo o estado da tela e piscando. Quem redireciona é a
 * `RotaProtegida`, ao ver a sessão vazia no próximo render.
 *
 * A consequência prática: uma rota autenticada registrada **fora** dela ficaria
 * parada na tela com a sessão morta, disparando 401 em silêncio. Rota nova que
 * exija login entra aqui dentro.
 *
 * `/selecionar-empresa` é a exceção que confirma a regra: exige sessão, mas não
 * empresa escolhida — é justamente onde a escolha acontece (RN-603).
 *
 * ## Por que o `ProvedorDeDialogos` envolve as rotas
 *
 * Toda confirmação ("apagar?", "cancelar com qual motivo?") é do sistema, não
 * do navegador — `useDialogo()` em vez de `window.confirm`. O provedor fica
 * dentro do `QueryClientProvider` (montado em `main.tsx`) e fora das rotas,
 * para que qualquer tela, pública ou autenticada, possa perguntar.
 */
export function App(): JSX.Element {
  return (
    <BrowserRouter>
      <LimiteDeErro area="a aplicação">
        <ProvedorDeDialogos>
          <Routes>
            <Route
              path="/"
              element={
                // O fundo evita o flash branco entre o chunk e a primeira pintura:
                // a LP é dark-first e a casca já entra na cor certa.
                <Suspense fallback={<div className="min-h-screen bg-superficie-base" />}>
                  <LandingPage />
                </Suspense>
              }
            />
            <Route path="/entrar" element={<Login />} />
            <Route path="/calculadora" element={<CalculadoraPublica />} />
            {/* Wizard público: `/cadastro` cria o rascunho, `/cadastro/:id` o retoma. */}
            {/* RN-103 — o link do e-mail. Sem casca do app: quem chega aqui não
              tem conta, e o resto do sistema só daria em "entre primeiro". */}
            <Route path="/acoes/:token" element={<AcaoPorLink />} />
            {/* O caminho é o que `urlPublicaDoLink` monta no backend. Sem esta
              rota, o convite caía no catch-all e ia parar no login — que é
              justamente o que o convidado ainda não tem. */}
            <Route path="/public/:tipo/:token" element={<FormularioPublico />} />
            <Route path="/recuperar-senha" element={<RecuperarSenha />} />
            {/* O link do cadastro de colaborador: a pessoa cria a própria senha. */}
            <Route path="/criar-senha/:token" element={<CriarSenha />} />
            <Route path="/cadastro" element={<CadastroPublico />} />
            <Route path="/cadastro/sucesso" element={<CadastroConcluido />} />
            <Route path="/cadastro/:registroId" element={<CadastroPublico />} />

            <Route element={<RotaProtegida exigeEmpresa={false} />}>
              <Route path="/selecionar-empresa" element={<SelecionarEmpresa />} />
            </Route>

            {/*
            Tudo que exige empresa escolhida vive sob a casca: navegação lateral,
            barra superior e limite de erro por tela. Rota nova de app entra aqui
            dentro — fora dela ficaria sem menu e sem o redirecionamento de sessão
            expirada.
          */}
            <Route element={<RotaProtegida />}>
              <Route element={<CascaDoApp />}>
                <Route path="/painel" element={<Painel />} />
                <Route path="/beca" element={<Beca />} />
                {/* A carteira de clientes virou Negociações do Recorrente; o
                  caminho antigo redireciona com a query (o `?situacao=` do sino). */}
                <Route
                  path="/clientes"
                  element={<RedirecionarComQuery para="/icms-recorrente/negociacoes" />}
                />
                <Route path="/clientes/:clienteId" element={<PerfilDoCliente />} />
                {/* "Meus clientes" saiu: os clientes do assessor aparecem dentro de
                  cada serviço (Negociações do Recorrente, Contratos do Passivo,
                  Negociações do Pontual). O caminho antigo leva ao Recorrente. */}
                <Route
                  path="/meus-clientes"
                  element={<Navigate to="/icms-recorrente/negociacoes" replace />}
                />
                <Route path="/fornecedores" element={<Carteira papel="fornecedores" />} />
                <Route path="/assessores" element={<Carteira papel="assessores" />} />
                {/* Para quem não é admin, as empresas dele: cadastro e colaboradores. */}
                <Route
                  path="/empresas"
                  element={<AdminOuParte admin={<Empresas />} parte={<EmpresasDaParte />} />}
                />
                <Route path="/empresas/:businessId" element={<EmpresaDetalhe />} />
                {/*
                As divisões de serviço. Cada uma tem a sua página de
                Configurações; o que era tela avulsa ("Aviso de SISCRED") virou
                aba dentro dela, e o caminho antigo redireciona para não quebrar
                favorito nem link de e-mail.
              */}
                <Route path="/simulacoes" element={<Captacao modo="simulacoes" />} />
                <Route path="/simulacoes/:registroId" element={<SimulacaoDoLead />} />
                <Route
                  path="/icms-recorrente/negociacoes"
                  element={
                    <Carteira
                      papel="clientes"
                      titulo="Negociações"
                      descricao="Empresas calculadas no ICMS Recorrente — por SPED ou lançamento manual — e a etapa do contrato de cada uma."
                      descricaoDaParte="As negociações do ICMS Recorrente da sua empresa — como cliente, assessora ou fornecedora — e a etapa do contrato de cada uma."
                    />
                  }
                />
                {/*
                  A Central de Configurações: as de cada divisão e as automações.
                  Os caminhos antigos de cada divisão caem nela, no serviço certo.
                */}
                <Route
                  path="/configuracoes"
                  element={
                    <AdminOuParte
                      admin={<CentralDeConfiguracoes />}
                      parte={<Navigate to="/painel" replace />}
                    />
                  }
                />
                <Route
                  path="/icms-recorrente/configuracoes"
                  element={<Navigate to="/configuracoes?servico=recorrente" replace />}
                />
                <Route
                  path="/aviso-siscred"
                  element={<Navigate to="/configuracoes?servico=recorrente" replace />}
                />
                {/* Simulações do Passivo: a do admin grava; a do assessor só calcula
                  e, se ele quiser, pede o fechamento à operação. */}
                <Route
                  path="/passivo"
                  element={
                    <AdminOuParte admin={<Passivo />} parte={<SimulacoesPassivoDaParte />} />
                  }
                />
                {/*
                  Contratos do Passivo e Negociações do Pontual são a mesma rota
                  para todos: o admin opera, as partes veem o que é delas, na
                  visão do papel (`AdminOuParte`). As Negociações do Recorrente
                  são a mesma tela para todos, com a lista no escopo de quem olha.
                */}
                <Route
                  path="/passivo/contratos"
                  element={<AdminOuParte admin={<PassivoContratos />} parte={<PassivoDaParte />} />}
                />
                <Route
                  path="/passivo/configuracoes"
                  element={<Navigate to="/configuracoes?servico=passivo" replace />}
                />
                <Route
                  path="/icms-pontual"
                  element={
                    <AdminOuParte admin={<NegociacoesPontual />} parte={<PontualDaParte />} />
                  }
                />
                {/* As antigas visões "Como cliente/fornecedor/assessor". */}
                <Route
                  path="/icms-recorrente/visao/:papel"
                  element={<Navigate to="/icms-recorrente/negociacoes" replace />}
                />
                <Route
                  path="/icms-pontual/configuracoes"
                  element={<Navigate to="/configuracoes?servico=pontual" replace />}
                />
                {/*
                "Contratos" e "Contratos assinados" saíram: a lista de PDFs
                gerados agora é uma seção da minuta do fornecedor, e a visão
                por cliente já vive no perfil dele. Os caminhos antigos caem no
                catch-all, que leva ao painel.
              */}
                <Route path="/aprovacao" element={<Captacao modo="aprovacao" />} />
                <Route path="/servicos" element={<Servicos />} />
                <Route path="/pessoas" element={<Pessoas />} />
                <Route path="/pessoas/:personId" element={<PessoaDetalhe />} />
                {/* CRM: o tópico leva à primeira tela dele. */}
                <Route path="/crm" element={<Navigate to="/crm/pipeline" replace />} />
                <Route path="/crm/pipeline" element={<Pipeline />} />
                <Route path="/crm/atividades" element={<PaginaDeAtividades />} />
                <Route path="/crm/relatorios" element={<PaginaDeRelatorios />} />
                <Route path="/crm/prospeccao" element={<PaginaDeProspeccao />} />
                <Route path="/notificacoes" element={<Notificacoes />} />
                <Route path="/perfil" element={<Perfil />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/painel" replace />} />
          </Routes>
        </ProvedorDeDialogos>
      </LimiteDeErro>
    </BrowserRouter>
  );
}

/** Redireciona para outro caminho levando a query junto — o `?situacao=` do sino. */
function RedirecionarComQuery(props: { readonly para: string }): JSX.Element {
  const { search } = useLocation();
  return <Navigate to={`${props.para}${search}`} replace />;
}
